'use client'

import Link from 'next/link'
import { AuthTier, tierPricing } from '@/contexts/AuthContext'

interface UpgradePromptProps {
  feature: string
  requiredTier: AuthTier
  variant?: 'inline' | 'modal' | 'banner'
  onDismiss?: () => void
}

const featureDescriptions: Record<string, string> = {
  'unlimited-watchlist': 'Add unlimited artists to your watchlist',
  '5-year-history': 'Access 5 years of historical price data',
  '30-year-history': 'Access complete 30-year historical data',
  'standard-ai': 'Get AI-powered price predictions and trend analysis',
  'full-ai': 'Access advanced AI insights including portfolio optimization',
  'export': 'Export data to CSV and PDF reports',
  'unlimited-alerts': 'Set unlimited price and artist alerts',
  'team-seats': 'Add team members to your account',
  'white-label': 'Create white-label reports for clients',
  'custom-integrations': 'Build custom integrations with our API',
}

const tierBenefits: Record<AuthTier, string[]> = {
  free: [],
  collector: [],
  gallery: [],
  enterprise: [],
  individual: [
    'Full auction history (50+ years)',
    'AI-powered market analysis',
    'Unlimited artist tracking',
    'Real-time alerts',
    'CSV & PDF exports',
  ],
  institutional: [
    'Everything in Individual',
    'API & MCP access',
    'Multi-seat team access',
    'Dedicated support',
    'Priority support',
    'Custom integrations',
  ],
}

export function UpgradePrompt({
  feature,
  requiredTier,
  variant = 'inline',
  onDismiss,
}: UpgradePromptProps) {
  const description = featureDescriptions[feature] || 'Access this feature'
  const pricing = tierPricing[requiredTier]
  const benefits = tierBenefits[requiredTier]
  const tierLabel = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)

  if (variant === 'inline') {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        {/* Lock icon */}
        <div
          className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ background: 'var(--accent-primary)', opacity: 0.1 }}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: 'var(--accent-primary)' }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>

        <h3 className="font-medium text-text-primary mb-2">
          Upgrade to {tierLabel}
        </h3>
        <p className="text-sm text-text-secondary mb-4">{description}</p>

        <Link
          href="/get-started"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-all hover:brightness-110"
          style={{ background: 'var(--accent-primary)' }}
        >
          Upgrade for ${pricing.price}{pricing.period}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    )
  }

  if (variant === 'banner') {
    return (
      <div
        className="flex items-center justify-between px-4 py-3 rounded-xl border"
        style={{
          background: 'var(--accent-primary)',
          opacity: 0.05,
          borderColor: 'var(--accent-primary)',
        }}
      >
        <div className="flex items-center gap-3">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: 'var(--accent-primary)' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          <p className="text-sm text-text-primary">
            <span className="font-medium">{description}</span>
            {' '}— Upgrade to {tierLabel}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            href="/get-started"
            className="px-4 py-1.5 rounded-full text-sm font-medium text-white"
            style={{ background: 'var(--accent-primary)' }}
          >
            Upgrade
          </Link>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1.5 rounded-lg hover:bg-page text-text-tertiary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    )
  }

  // Modal variant
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-text-primary/50 backdrop-blur-sm"
        onClick={onDismiss}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-2xl border border-border max-w-md w-full p-8">
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-page text-text-tertiary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Lock icon */}
        <div
          className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
          style={{ background: 'var(--accent-primary)' }}
        >
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>

        <h2 className="font-display text-2xl font-light text-center mb-2">
          Upgrade to {tierLabel}
        </h2>
        <p className="text-text-secondary text-center mb-6">{description}</p>

        {/* Benefits list */}
        <ul className="space-y-3 mb-8">
          {benefits.map((benefit) => (
            <li key={benefit} className="flex items-center gap-3 text-sm">
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                style={{ color: 'var(--positive)' }}
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-text-primary">{benefit}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <Link
          href="/get-started"
          className="block w-full text-center py-3 rounded-full text-white font-medium transition-all hover:brightness-110"
          style={{ background: 'var(--accent-primary)' }}
        >
          Upgrade for ${pricing.price}{pricing.period}
        </Link>

        <p className="text-xs text-text-tertiary text-center mt-4">
          Cancel anytime. No long-term commitment.
        </p>
      </div>
    </div>
  )
}
